import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Building2, Mail, Phone, DollarSign, Send, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const Partnerships = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    inquiry_type: "sponsorship",
    budget_range: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partnerships/inquire`, form);
      setSubmitted(true);
      toast.success("Inquiry submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#EC008C]" />
          </div>
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-4">Thank You!</h1>
          <p className="text-[#8A92A0] text-lg mb-8">
            Our partnerships team will review your inquiry and respond within 48 hours.
          </p>
          <Link to="/home">
            <Button className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]" data-testid="back-home-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="partnerships-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EC008C]/10 border border-[#EC008C]/30 mb-6">
              <Building2 className="w-4 h-4 text-[#EC008C]" />
              <span className="text-sm text-[#EC008C] font-medium">Sponsor Partnerships</span>
            </div>
            <h1 className="font-['Unbounded'] text-4xl sm:text-5xl font-bold text-white mb-4">
              Partner With <span className="text-[#EC008C]">StardomFans</span>
            </h1>
            <p className="text-[#8A92A0] text-lg max-w-xl mx-auto">
              Reach millions of passionate fans through brand sponsorships, artist collaborations, and exclusive activations.
            </p>
          </div>

          {/* Partnership Types */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
            {[
              { type: "sponsorship", label: "Sponsorship", desc: "Sponsor events, artists, and content" },
              { type: "brand_deal", label: "Brand Deal", desc: "Co-branded products and campaigns" },
              { type: "referral", label: "Referral", desc: "Affiliate and referral partnerships" },
              { type: "custom", label: "Custom", desc: "Tell us your idea" }
            ].map((opt) => (
              <button
                key={opt.type}
                onClick={() => setForm({ ...form, inquiry_type: opt.type })}
                className={`p-4 rounded-xl border text-left transition-all ${
                  form.inquiry_type === opt.type
                    ? "bg-[#EC008C]/10 border-[#EC008C] text-[#EC008C]"
                    : "bg-[#0A0E18] border-[#1A2030] text-[#8A92A0] hover:border-[#EC008C]/40"
                }`}
                data-testid={`partnership-type-${opt.type}`}
              >
                <p className="font-semibold text-white mb-1">{opt.label}</p>
                <p className="text-sm">{opt.desc}</p>
              </button>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="card p-8 space-y-6" data-testid="partnership-form">
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Tell Us About Your Brand</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Company Name *</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="bg-[#050810] border-[#1A2030] text-white pl-10"
                    placeholder="Acme Corp"
                    required
                    data-testid="partnership-company-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white">Contact Name *</Label>
                <Input
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="bg-[#050810] border-[#1A2030] text-white mt-1"
                  placeholder="Jane Smith"
                  required
                  data-testid="partnership-contact-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-[#050810] border-[#1A2030] text-white pl-10"
                    placeholder="jane@acme.com"
                    required
                    data-testid="partnership-email-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white">Phone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-[#050810] border-[#1A2030] text-white pl-10"
                    placeholder="+1 555-0123"
                    data-testid="partnership-phone-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-white">Budget Range</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A7280]" />
                <Input
                  value={form.budget_range}
                  onChange={(e) => setForm({ ...form, budget_range: e.target.value })}
                  className="bg-[#050810] border-[#1A2030] text-white pl-10"
                  placeholder="$5,000 - $50,000"
                  data-testid="partnership-budget-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-white">Message *</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-[#050810] border-[#1A2030] text-white mt-1"
                placeholder="Tell us about your brand, goals, and how you'd like to partner..."
                rows={5}
                required
                data-testid="partnership-message-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] py-3 text-base font-semibold"
              data-testid="partnership-submit-btn"
            >
              {loading ? "Submitting..." : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Submit Partnership Inquiry
                </>
              )}
            </Button>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default Partnerships;
